import { useEffect, useMemo, useRef } from 'react';

import { isResourceName } from '@spotify/confidence-core-react';
import {
  useAttachments,
  useCreateAttachment,
} from '@spotify/plugin-confidence-attachments-react';
import {
  type ToolGroupEntry,
  createToolExtension,
  mcpTool,
} from '@spotify/plugin-confidence-chat-react';
import {
  type CapturedBlock,
  type PulseCaptureResult,
  usePulseBlockCaptures,
} from '@spotify/plugin-confidence-pulse-react';

import { confidenceChatPlugin } from '../plugin';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getSourceChatName = (eventName?: string): string | undefined => {
  const eventPathIndex = eventName?.indexOf('/events/') ?? -1;
  return eventPathIndex >= 0 ? eventName?.slice(0, eventPathIndex) : undefined;
};

const getCaptureResultState = (
  resultSucceeded?: boolean,
): PulseCaptureResult['state'] => {
  if (resultSucceeded === undefined) return 'pending';
  return resultSucceeded ? 'successful' : 'failed';
};

export const parseSaveToPulseCaptures = (content: string): CapturedBlock[] => {
  let result: unknown;
  try {
    result = JSON.parse(content);
  } catch {
    return [];
  }

  if (!isRecord(result) || !isRecord(result.pulse)) return [];
  const pulseName = result.pulse.name;
  const pulseDisplayName = result.pulse.display_name;
  if (typeof pulseName !== 'string' || !Array.isArray(result.blocks)) return [];

  return result.blocks.flatMap(block => {
    if (
      !isRecord(block) ||
      block.status !== 'captured' ||
      typeof block.block !== 'string' ||
      typeof block.display_name !== 'string'
    ) {
      return [];
    }

    return [
      {
        pulseName,
        pulseDisplayName:
          typeof pulseDisplayName === 'string' ? pulseDisplayName : undefined,
        blockName: block.block,
        blockDisplayName: block.display_name,
      },
    ];
  });
};

export const SaveToPulseToolCapture = ({
  entry,
}: {
  entry: ToolGroupEntry;
}) => {
  const { observeCaptureResult, projectName } = usePulseBlockCaptures();
  const { createAttachment } = useCreateAttachment();
  const resultContent = entry.result?.content;
  const resultSucceeded = entry.result?.success;
  const captureResultState = getCaptureResultState(resultSucceeded);
  const createdAttachmentsRef = useRef(new Set<string>());
  const captures = useMemo(
    () =>
      resultSucceeded && resultContent
        ? parseSaveToPulseCaptures(resultContent)
        : [],
    [resultContent, resultSucceeded],
  );

  const sourceChat = getSourceChatName(entry.eventName);
  const attachmentResource =
    projectName ??
    (sourceChat && isResourceName(sourceChat, 'chats')
      ? sourceChat
      : undefined);

  const { attachments: existingAttachments, isLoading: isLoadingAttachments } =
    useAttachments({
      resource: attachmentResource ?? '',
      pageSize: 100,
      skip: !attachmentResource,
    });

  useEffect(() => {
    observeCaptureResult({
      captures,
      eventName: entry.eventName,
      state: captureResultState,
    });
  }, [captures, captureResultState, entry.eventName, observeCaptureResult]);

  useEffect(() => {
    if (!resultSucceeded) return;
    if (!attachmentResource || isLoadingAttachments) return;
    const pulseNames = [...new Set(captures.map(c => c.pulseName))];
    for (const pulseName of pulseNames) {
      if (createdAttachmentsRef.current.has(pulseName)) continue;
      if (existingAttachments?.some(a => a.uri === pulseName)) continue;
      createdAttachmentsRef.current.add(pulseName);
      const displayName =
        captures.find(c => c.pulseName === pulseName)?.pulseDisplayName ?? '';
      void createAttachment({
        resource: attachmentResource,
        uri: pulseName,
        displayName,
      }).catch(() => {});
    }
  }, [
    attachmentResource,
    captures,
    createAttachment,
    existingAttachments,
    isLoadingAttachments,
    resultSucceeded,
  ]);

  return <>{entry.displayName ?? 'Save to pulse'}</>;
};

export const SaveToPulseToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'save-to-pulse',
    predicate: mcpTool('save_to_pulse'),
    display: 'grouped',
    collapseConsecutiveCalls: false,
    render: () => null,
    renderLabel: entry => <SaveToPulseToolCapture entry={entry} />,
  }),
);
